import React, { useState, useEffect } from 'react';
import { wardrobeAPI } from '../api';
import { useWebSocket } from '../context/WebSocketContext';

function HistoryPage() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { activeJobs } = useWebSocket();

  useEffect(() => {
    loadHistory();
  }, []);

  // Auto-reload when try-on jobs complete
  useEffect(() => {
    const tryOnJobs = Array.from(activeJobs.values()).filter(
      job => job.job_type === 'try_on'
    );
    const wasProcessing = tryOnJobs.some(job => job.status === 'processing');
    const allCompleted = tryOnJobs.every(job => job.status === 'completed');
    
    // Reload when jobs transition from processing to all completed
    if (wasProcessing && allCompleted) {
      loadHistory();
    }
  }, [activeJobs]);

  const loadHistory = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await wardrobeAPI.getHistory(50);
      console.log('History response:', response.data);
      const historyData = Array.isArray(response.data) ? response.data : [];
      setHistory(historyData);
    } catch (err) {
      console.error('Failed to load history:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to load try-on history';
      setError(typeof errorMsg === 'string' ? errorMsg : 'Failed to load try-on history');
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (item) => {
    setSelectedImage(item);
    setShowModal(true);
  };

  return (
    <div 
      className="container"
      style={{ 
        backgroundImage: `url(${process.env.PUBLIC_URL}/assets/branding/hero-fashion.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      <h1>My Try-On History</h1>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="card">
        <div className="history-header">
          <h3>Recent Try-Ons ({history.length})</h3>
          <button className="btn btn-secondary" onClick={loadHistory} disabled={loading}>
            🔄 Refresh
          </button>
        </div>
        
        {loading ? (
          <div className="loading-spinner" />
        ) : history.length === 0 ? (
          <p className="text-center">No try-on history yet. Try on some clothes first!</p>
        ) : (
          <div className="history-grid">
            {history.map((item) => (
              <div 
                key={item.id} 
                className="history-item"
                onClick={() => handleImageClick(item)}
              >
                <div className="history-image-container">
                  <img src={item.result_url} alt="Try-on result" />
                  <div className="history-overlay">
                    <span>👁️ View</span>
                  </div>
                </div>
                <div className="history-info">
                  <p className="text-muted">
                    {new Date(item.created_at).toLocaleDateString()} at {new Date(item.created_at).toLocaleTimeString()}
                  </p>
                  {item.processing_time && (
                    <p className="processing-time">⚡ {item.processing_time.toFixed(2)}s</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Image Modal */}
      {showModal && selectedImage && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            <h2>Try-On Result</h2>
            
            <div className="history-detail">
              <div className="result-container">
                <img src={selectedImage.result_url} alt="Try-on result" />
              </div>
              
              <div className="history-metadata">
                <p><strong>Date:</strong> {new Date(selectedImage.created_at).toLocaleString()}</p>
                {selectedImage.processing_time && (
                  <p><strong>Processing Time:</strong> {selectedImage.processing_time.toFixed(2)} seconds</p>
                )}
                {selectedImage.person_image_url && (
                  <div className="person-preview">
                    <p><strong>Original Photo:</strong></p>
                    <img src={selectedImage.person_image_url} alt="Original" style={{ maxWidth: '200px', borderRadius: '8px', marginTop: '0.5rem' }} />
                  </div>
                )}
              </div>
            </div>
            
            <div className="mt-2">
              <a 
                href={selectedImage.result_url} 
                download 
                className="btn btn-primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                📥 Download Image
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HistoryPage;
